import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import BrandHeading from "@/components/brand/BrandHeading";
import BrandLabel from "@/components/brand/BrandLabel";
import BrandButton from "@/components/brand/BrandButton";
import BrandCard from "@/components/brand/BrandCard";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { listBookings, listEnquiries, cancelBooking } from "@/lib/api";
import { useAuth } from "@/contexts/AuthProvider";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import {
  ChevronRight,
  ExternalLink,
  CalendarRange,
  XCircle,
  Download,
  Mail,
  MessageSquare,
  FileText,
} from "lucide-react";

// ---------------------------------------------------------------------------
// Styling helpers (kept consistent with the rest of GMH styling on this page)
// ---------------------------------------------------------------------------
const statusTone = {
  confirmed: "bg-emerald-50 text-emerald-800 border border-emerald-200",
  cancelled: "bg-rose-50 text-rose-800 border border-rose-200",
  pending:   "bg-amber-50 text-amber-800 border border-amber-200",
  past:      "bg-slate-100 text-slate-700 border border-slate-200",
  // Enquiry pipeline tones
  new:       "bg-sky-50 text-sky-800 border border-sky-200",
  in_review: "bg-amber-50 text-amber-800 border border-amber-200",
  contacted: "bg-violet-50 text-violet-800 border border-violet-200",
  closed:    "bg-slate-100 text-slate-700 border border-slate-200",
  converted: "bg-emerald-50 text-emerald-800 border border-emerald-200",
};

// Map the operator pipeline enum to the human-facing pipeline labels the user
// asked us to present on the guest side ("New / In Conversation / Proposal Sent
// / Confirmed / Declined").
const enquiryStatusLabel = {
  new:       "New",
  in_review: "In Conversation",
  contacted: "Proposal Sent",
  converted: "Confirmed",
  closed:    "Declined",
};

const brandTint = {
  castlewood: "border-l-4 border-l-[#5C2730]",
  vela: "border-l-4 border-l-[#E07856]",
  brionj: "border-l-4 border-l-[#C5B188]",
};

// ---------------------------------------------------------------------------
// Pricing helpers
// ---------------------------------------------------------------------------
// Fallback heuristic for legacy bookings whose stored extras lack a per_night
// flag.  Matches the backend's _is_per_night logic so display stays consistent.
const isPerNight = (e) => {
  if (typeof e?.per_night === "boolean") return e.per_night;
  const n = (e?.name || "").toLowerCase();
  return /\bdaily\b|per night|per-night|\/n\b/.test(n);
};
const extraLineTotal = (e, nights) =>
  (e?.line_total ?? (isPerNight(e) ? (e.price || 0) * nights : (e.price || 0)));

// Best-effort booking status: if it's still confirmed but check-out is in the
// past, present it as "past" for the badge tone.  Server status is the source
// of truth for actions (only `confirmed` allows cancel/modify).
const displayStatus = (b) => {
  if (b.status === "cancelled") return "cancelled";
  if (b.status !== "confirmed") return b.status || "pending";
  try {
    const co = new Date(b.check_out + "T00:00:00");
    if (co < new Date(new Date().toDateString())) return "past";
  } catch (err) {
    // Defensive parse — malformed check_out string falls back to
    // "confirmed". Log so unexpected shapes don't disappear silently.
    // eslint-disable-next-line no-console
    console.warn("[Bookings] could not parse check_out date:", b.check_out, err);
  }
  return "confirmed";
};

// Pretty date — friendlier than the ISO format on the list.
const fmtDate = (s) => {
  if (!s) return "—";
  try {
    return new Date(s + "T00:00:00").toLocaleDateString(undefined, {
      day: "numeric", month: "short", year: "numeric",
    });
  } catch { return s; }
};

const Bookings = () => {
  const { user, loading: authLoading } = useAuth();
  const [bookings, setBookings] = useState([]);
  const [enquiries, setEnquiries] = useState([]);
  const [loading, setLoading] = useState(true);

  // Detail panel state
  const [openBookingId, setOpenBookingId] = useState(null);
  const [openEnquiryId, setOpenEnquiryId] = useState(null);
  // Cancel confirmation dialog state
  const [confirmCancelId, setConfirmCancelId] = useState(null);

  const refresh = async () => {
    setLoading(true);
    try {
      const [b, e] = await Promise.all([listBookings(), listEnquiries()]);
      setBookings(b);
      setEnquiries(e);
    } catch (err) {
      // Surface in the console so the failure is debuggable in DevTools.
      // We deliberately keep the UI quiet here — Bookings.jsx degrades to
      // an empty list, which the empty-state copy below handles.
      // eslint-disable-next-line no-console
      console.error("[Bookings] refresh failed:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!user) { setLoading(false); return; }
    refresh();
  }, [user]);

  const onCancel = async (id) => {
    try {
      await cancelBooking(id);
      toast("Booking cancelled");
      setConfirmCancelId(null);
      refresh();
    } catch {
      toast.error("Couldn't cancel booking");
    }
  };

  // Build the active booking + enquiry (for the side panels) from the latest lists.
  const openBooking = bookings.find((b) => b.id === openBookingId) || null;
  const openEnquiry = enquiries.find((e) => e.id === openEnquiryId) || null;

  if (authLoading) return <div className="container-rail py-16">Loading…</div>;

  if (!user) {
    return (
      <div className="container-rail py-16 md:py-24" data-testid="bookings-signed-out">
        <BrandLabel withRule>Your stays</BrandLabel>
        <BrandHeading level={1} className="mt-4">Sign in to see your stays</BrandHeading>
        <p className="mt-4 max-w-[52ch] text-muted-foreground">Bookings across Castlewood and Vela, and Brionj enquiries, appear here once you sign in.</p>
        <div className="mt-8">
          <BrandButton to="/auth/sign-in" data-testid="signin-cta">Sign in</BrandButton>
        </div>
      </div>
    );
  }

  return (
    <div className="container-rail py-10 md:py-16" data-testid="bookings-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <BrandLabel withRule>Your account</BrandLabel>
          <BrandHeading level={1} className="mt-3">Your stays</BrandHeading>
          <p className="mt-3 max-w-[58ch] text-muted-foreground">Bookings with Castlewood and Vela, and enquiries with Brionj, all in one place.</p>
        </div>
        <BrandCard className="p-5 min-w-[260px]" data-testid="loyalty-widget">
          <BrandLabel>GMH Membership</BrandLabel>
          <div className="mt-3 text-[18px] font-display">{user.loyalty?.tier || "Atlas"}</div>
          <div className="text-[12px] text-muted-foreground mt-1">{user.loyalty?.points || 0} points</div>
          <div className="mt-3 h-1.5 bg-muted rounded-full overflow-hidden">
            <div className="h-full bg-foreground" style={{ width: `${Math.min(100, ((user.loyalty?.points || 0) / (user.loyalty?.next_tier_points || 1)) * 100)}%` }} />
          </div>
          <div className="text-[11px] text-muted-foreground mt-2">{Math.max(0, (user.loyalty?.next_tier_points || 0) - (user.loyalty?.points || 0))} points to {user.loyalty?.next_tier || "—"}</div>
        </BrandCard>
      </div>

      <Tabs defaultValue="bookings" className="mt-10">
        <TabsList data-testid="bookings-tabs">
          <TabsTrigger value="bookings" data-testid="tab-bookings">Bookings ({bookings.length})</TabsTrigger>
          <TabsTrigger value="enquiries" data-testid="tab-enquiries">Brionj Enquiries ({enquiries.length})</TabsTrigger>
        </TabsList>

        {/* ----------------------------- Bookings ----------------------------- */}
        <TabsContent value="bookings" className="mt-6">
          {loading ? <p>Loading…</p> : bookings.length === 0 ? (
            <BrandCard className="p-10 text-center text-muted-foreground" data-testid="no-bookings">No bookings yet. <Link to="/search" className="underline ml-1">Find a stay</Link>.</BrandCard>
          ) : (
            <div className="grid gap-4">
              {bookings.map((b) => {
                const status = displayStatus(b);
                return (
                  <BrandCard
                    key={b.id}
                    className={cn(
                      "p-5 flex flex-col md:flex-row gap-5 cursor-pointer transition-colors hover:border-foreground/40",
                      brandTint[b.brand]
                    )}
                    data-testid={`booking-row-${b.id}`}
                    onClick={() => setOpenBookingId(b.id)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        setOpenBookingId(b.id);
                      }
                    }}
                  >
                    <img src={b.property_image} alt={b.property_name} className="w-full md:w-44 h-32 object-cover rounded-[var(--radius)]" />
                    <div className="flex-1 min-w-0">
                      <BrandLabel>{b.brand?.toUpperCase()} · {b.property_location}</BrandLabel>
                      <div className="text-[20px] font-display mt-1 truncate">{b.property_name}</div>
                      <div className="text-[13px] text-muted-foreground mt-1">
                        {b.room_name} · {fmtDate(b.check_in)} → {fmtDate(b.check_out)} · {b.nights} night{b.nights !== 1 && "s"} · {b.guests} guest{b.guests !== 1 && "s"}
                      </div>
                      <div className="mt-3 inline-flex items-center gap-1 text-[11px] uppercase tracking-[0.18em] text-muted-foreground group-hover:text-foreground" data-testid={`booking-view-details-${b.id}`}>
                        View details <ChevronRight size={12} />
                      </div>
                    </div>
                    <div className="flex flex-col items-end justify-between gap-3 min-w-[150px]">
                      <Badge className={statusTone[status] || statusTone.confirmed}>{status}</Badge>
                      <div className="text-right">
                        <div className="text-[18px] font-display">€{Math.round(b.total)}</div>
                        {status === "confirmed" && (
                          <button
                            onClick={(ev) => { ev.stopPropagation(); setConfirmCancelId(b.id); }}
                            className="text-[12px] underline text-muted-foreground hover:text-destructive mt-1"
                            data-testid={`cancel-${b.id}`}
                          >
                            Cancel booking
                          </button>
                        )}
                      </div>
                    </div>
                  </BrandCard>
                );
              })}
            </div>
          )}
        </TabsContent>

        {/* ----------------------------- Enquiries ----------------------------- */}
        <TabsContent value="enquiries" className="mt-6">
          {loading ? <p>Loading…</p> : enquiries.length === 0 ? (
            <BrandCard className="p-10 text-center text-muted-foreground" data-testid="no-enquiries">No Brionj enquiries yet. <Link to="/brionj/collection" className="underline ml-1">Browse residences</Link>.</BrandCard>
          ) : (
            <div className="grid gap-4">
              {enquiries.map((e) => (
                <BrandCard
                  key={e.id}
                  className={cn(
                    "p-5 flex flex-col md:flex-row gap-5 cursor-pointer transition-colors hover:border-foreground/40",
                    brandTint.brionj
                  )}
                  data-testid={`enquiry-row-${e.id}`}
                  onClick={() => setOpenEnquiryId(e.id)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(ev) => {
                    if (ev.key === "Enter" || ev.key === " ") {
                      ev.preventDefault();
                      setOpenEnquiryId(e.id);
                    }
                  }}
                >
                  <img src={e.residence_image} alt={e.residence_name} className="w-full md:w-44 h-32 object-cover rounded-none" />
                  <div className="flex-1 min-w-0">
                    <BrandLabel>BRIONJ · {e.residence_location}</BrandLabel>
                    <div className="text-[20px] font-display italic mt-1 truncate">{e.residence_name}</div>
                    <div className="text-[13px] text-muted-foreground mt-1">{e.bedrooms} bedrooms · {e.guests} guests {e.check_in && `· ${fmtDate(e.check_in)} → ${fmtDate(e.check_out)}`}</div>
                    {e.notes && <p className="text-[13px] mt-2 max-w-[56ch] line-clamp-2">{e.notes}</p>}
                    <div className="mt-3 inline-flex items-center gap-1 text-[11px] uppercase tracking-[0.18em] text-muted-foreground" data-testid={`enquiry-view-details-${e.id}`}>
                      View details <ChevronRight size={12} />
                    </div>
                  </div>
                  <div className="flex flex-col items-end justify-between gap-3 min-w-[160px]">
                    <Badge className={statusTone[e.status] || statusTone.new}>
                      {enquiryStatusLabel[e.status] || (e.status || "").replace("_", " ")}
                    </Badge>
                    {e.budget && <div className="text-[12px] text-muted-foreground">{e.budget}</div>}
                  </div>
                </BrandCard>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* --------------------------- Booking detail Sheet --------------------------- */}
      <Sheet open={!!openBookingId} onOpenChange={(v) => !v && setOpenBookingId(null)}>
        <SheetContent
          side="right"
          className="w-full sm:max-w-[560px] overflow-y-auto"
          data-testid="booking-detail-sheet"
        >
          {openBooking && (
            <BookingDetail
              b={openBooking}
              onCancel={() => setConfirmCancelId(openBooking.id)}
              onClose={() => setOpenBookingId(null)}
            />
          )}
        </SheetContent>
      </Sheet>

      {/* --------------------------- Enquiry detail Sheet --------------------------- */}
      <Sheet open={!!openEnquiryId} onOpenChange={(v) => !v && setOpenEnquiryId(null)}>
        <SheetContent
          side="right"
          className="w-full sm:max-w-[560px] overflow-y-auto"
          data-testid="enquiry-detail-sheet"
        >
          {openEnquiry && (
            <EnquiryDetail
              e={openEnquiry}
              onClose={() => setOpenEnquiryId(null)}
            />
          )}
        </SheetContent>
      </Sheet>

      {/* ------------------- Cancel confirmation dialog (shared) ------------------- */}
      <AlertDialog
        open={!!confirmCancelId}
        onOpenChange={(v) => !v && setConfirmCancelId(null)}
      >
        <AlertDialogContent data-testid="confirm-cancel-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Cancel this booking?</AlertDialogTitle>
            <AlertDialogDescription>
              This will mark your reservation as cancelled. You can always book the property again from its page.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="confirm-cancel-no">Keep booking</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => onCancel(confirmCancelId)}
              data-testid="confirm-cancel-yes"
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Cancel booking
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Note: The "Withdraw enquiry" affordance has been intentionally
          removed from the guest /bookings view. The backend endpoint
          (POST /api/enquiries/{id}/withdraw) is still available and is
          exposed via lib/api `withdrawEnquiry`, but guests are expected to
          withdraw by contacting the Brionj house manager instead. */}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Booking detail view (rendered inside the side-panel Sheet)
// ---------------------------------------------------------------------------
const BookingDetail = ({ b, onCancel, onClose }) => {
  const status = displayStatus(b);
  const nights = b.nights || 1;
  const subtotal = b.subtotal ?? Math.max(0, (b.total || 0) - (b.extras_total || 0));
  const extrasTotal =
    b.extras_total ??
    (b.extras || []).reduce((acc, e) => acc + extraLineTotal(e, nights), 0);
  const nightlyRate = nights ? Math.round(subtotal / nights) : 0;
  const taxes = 0; // taxes-included prototype — surfaced as a row for transparency
  const total = b.total || subtotal + extrasTotal + taxes;

  // Lightweight invoice: a self-contained text blob the guest can save.  Real
  // invoicing would go through the back-office; this keeps the UI honest about
  // the action existing while not pretending we have a PDF service wired up.
  const downloadInvoice = () => {
    const lines = [
      `GRAND METROPOLITAN HOTELS — Booking Invoice`,
      `Reference: ${b.id}`,
      ``,
      `Property: ${b.property_name}`,
      `Location: ${b.property_location || ""}`,
      `Room:     ${b.room_name}`,
      `Guest:    ${b.guest_name} (${b.guest_email})`,
      `Party:    ${b.guests} guest${b.guests !== 1 ? "s" : ""}`,
      `Stay:     ${b.check_in} → ${b.check_out}  (${nights} night${nights !== 1 ? "s" : ""})`,
      `Status:   ${b.status}`,
      ``,
      `--- Price breakdown ---`,
      `${b.room_name}  €${nightlyRate} × ${nights} = €${subtotal}`,
      ...((b.extras || []).map((e) =>
        isPerNight(e)
          ? `${(e.name || "").replace(/<[^>]+>/g, "")}  €${e.price} × ${nights} = €${extraLineTotal(e, nights)}`
          : `${(e.name || "").replace(/<[^>]+>/g, "")}  €${e.price}`
      )),
      `Taxes: included`,
      `TOTAL: €${Math.round(total)}`,
      ``,
      `Thank you for staying with us.`,
    ];
    const blob = new Blob([lines.join("\n")], { type: "text/plain;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `gmh-booking-${b.id.slice(0, 8)}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const contactProperty = () => {
    const subject = encodeURIComponent(`Booking ${b.id.slice(0, 8)} — ${b.property_name}`);
    const body = encodeURIComponent(
      `Hello ${b.property_name} team,\n\nRegarding reservation ${b.id}\nDates: ${b.check_in} → ${b.check_out}\nGuest: ${b.guest_name}\n\n`
    );
    window.location.href = `mailto:reservations@gmh-hospitality.com?subject=${subject}&body=${body}`;
  };

  return (
    <div className="space-y-6" data-testid="booking-detail">
      <SheetHeader className="text-left space-y-2">
        <BrandLabel>
          {b.brand?.toUpperCase()} · Booking
        </BrandLabel>
        <SheetTitle className="font-display text-[26px] leading-tight">
          {b.property_name}
        </SheetTitle>
        <SheetDescription className="text-[13px]">
          {b.property_location}
        </SheetDescription>
        <div className="pt-2 flex items-center gap-3">
          <Badge className={statusTone[status] || statusTone.confirmed}>
            {status}
          </Badge>
          <span className="font-mono text-[11px] text-muted-foreground" data-testid="detail-reference">
            REF · {b.id}
          </span>
        </div>
      </SheetHeader>

      <img
        src={b.property_image}
        alt={b.property_name}
        className="w-full aspect-[16/9] object-cover rounded-[var(--radius)] border border-border"
      />

      {/* ----------------- Key facts ----------------- */}
      <section className="grid grid-cols-2 gap-4 text-[13px]">
        <Fact label="Check-in"   value={fmtDate(b.check_in)} testid="detail-checkin" />
        <Fact label="Check-out"  value={fmtDate(b.check_out)} testid="detail-checkout" />
        <Fact label="Nights"     value={`${nights} night${nights !== 1 ? "s" : ""}`} testid="detail-nights" />
        <Fact label="Party size" value={`${b.guests} guest${b.guests !== 1 ? "s" : ""}`} testid="detail-party" />
        <Fact label="Guest"      value={b.guest_name} testid="detail-guest" />
        <Fact label="Room"       value={b.room_name} testid="detail-room" />
      </section>

      <Separator />

      {/* ----------------- Price breakdown ----------------- */}
      <section data-testid="detail-breakdown">
        <BrandLabel>Price breakdown</BrandLabel>
        <div className="mt-3 space-y-2 text-[13px]">
          <div className="flex justify-between">
            <span className="text-muted-foreground">
              {b.room_name} · €{nightlyRate} × {nights} night{nights !== 1 ? "s" : ""}
            </span>
            <span>€{subtotal}</span>
          </div>

          {(b.extras || []).map((e) => (
            <div key={e.id || e.name} className="flex justify-between" data-testid={`detail-extra-${e.id || "x"}`}>
              <span className="text-muted-foreground">
                {e.name}
                {isPerNight(e)
                  ? ` · €${e.price} × ${nights} night${nights !== 1 ? "s" : ""}`
                  : ""}
              </span>
              <span>€{extraLineTotal(e, nights)}</span>
            </div>
          ))}

          <div className="flex justify-between">
            <span className="text-muted-foreground">Taxes &amp; fees</span>
            <span className="text-muted-foreground">included</span>
          </div>

          <Separator className="my-2" />
          <div className="flex justify-between font-display text-[18px]">
            <span>Total</span>
            <span data-testid="detail-total">€{Math.round(total)}</span>
          </div>
        </div>
      </section>

      <Separator />

      {/* ----------------- Payment & requests ----------------- */}
      <section className="grid grid-cols-2 gap-4 text-[13px]" data-testid="detail-payment">
        <Fact label="Payment status" value={b.status === "cancelled" ? "Refund pending" : "Paid"} />
        <Fact label="Payment method" value={`Visa ···· ${(b.id.replace(/[^0-9]/g, "").slice(-4) || "4242").padStart(4, "0")}`} />
      </section>

      {(b.special_requests && b.special_requests.trim().length > 0) ? (
        <section data-testid="detail-requests">
          <BrandLabel>Special requests</BrandLabel>
          <p className="mt-2 text-[13px] text-foreground/85 whitespace-pre-line">
            {b.special_requests}
          </p>
        </section>
      ) : null}

      {/* ----------------- Actions ----------------- */}
      <Separator />
      <section className="flex flex-wrap gap-2" data-testid="detail-actions">
        <Link
          to={`/${b.brand}/p/${b.property_id}`}
          className="text-[11px] uppercase tracking-[0.18em] inline-flex items-center gap-1.5 px-3 py-2 border border-border rounded-[var(--radius)] hover:border-foreground/40 hover:text-foreground transition-colors"
          onClick={onClose}
          data-testid="detail-view-property"
        >
          View property <ExternalLink size={12} />
        </Link>
        {status === "confirmed" && (
          <button
            type="button"
            className="text-[11px] uppercase tracking-[0.18em] inline-flex items-center gap-1.5 px-3 py-2 border border-border rounded-[var(--radius)] hover:border-foreground/40 hover:text-foreground transition-colors"
            onClick={() => toast("Date-modification will be available shortly. Please contact the property to change dates.")}
            data-testid="detail-modify-dates"
          >
            Modify dates <CalendarRange size={12} />
          </button>
        )}
        <button
          type="button"
          className="text-[11px] uppercase tracking-[0.18em] inline-flex items-center gap-1.5 px-3 py-2 border border-border rounded-[var(--radius)] hover:border-foreground/40 hover:text-foreground transition-colors"
          onClick={downloadInvoice}
          data-testid="detail-download-invoice"
        >
          Download invoice <Download size={12} />
        </button>
        <button
          type="button"
          className="text-[11px] uppercase tracking-[0.18em] inline-flex items-center gap-1.5 px-3 py-2 border border-border rounded-[var(--radius)] hover:border-foreground/40 hover:text-foreground transition-colors"
          onClick={contactProperty}
          data-testid="detail-contact-property"
        >
          Contact the property <Mail size={12} />
        </button>
        {status === "confirmed" && (
          <button
            type="button"
            className="text-[11px] uppercase tracking-[0.18em] inline-flex items-center gap-1.5 px-3 py-2 border border-destructive/30 text-destructive rounded-[var(--radius)] hover:bg-destructive/10 transition-colors"
            onClick={onCancel}
            data-testid="detail-cancel-booking"
          >
            Cancel booking <XCircle size={12} />
          </button>
        )}
      </section>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Brionj Enquiry detail view (rendered inside the side-panel Sheet)
// Mirrors the BookingDetail layout/typography/dismiss so the two surfaces feel
// like the same component family; only the field set + status chip + actions
// shift to the enquiry-led flow.
// ---------------------------------------------------------------------------
const EnquiryDetail = ({ e, onClose }) => {
  const status = e.status || "new";
  const statusLabel = enquiryStatusLabel[status] || status;
  const nights = (() => {
    if (!e.check_in || !e.check_out) return 0;
    const a = new Date(e.check_in + "T00:00:00");
    const b = new Date(e.check_out + "T00:00:00");
    return Math.max(0, Math.round((b - a) / (1000 * 60 * 60 * 24)));
  })();

  // The enquiry can already carry an updated_at if the operator has touched
  // it; otherwise fall back to created_at so the timeline is never empty.
  const submittedDate = e.created_at ? new Date(e.created_at) : null;
  const updatedDate   = e.updated_at ? new Date(e.updated_at) : null;
  const fmtFull = (d) =>
    d ? d.toLocaleString(undefined, {
      day: "numeric", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    }) : "—";

  // The brief asks for "Assigned house manager or named contact at Brionj (if
  // assigned)".  We don't track an explicit assignee on the model yet; show a
  // default house manager once the enquiry has been picked up (status moves
  // beyond `new`), otherwise show "Pending assignment".
  const houseManager =
    e.assigned_to ||
    (status === "new"
      ? null
      : "Helena Bauer · Brionj House Manager");

  // Build the activity log from whatever the model carries.  We always have a
  // "submitted" entry; we synthesise the pipeline transitions so the guest can
  // see at a glance where their enquiry stands.
  const timeline = [];
  timeline.push({
    when: submittedDate,
    title: "Enquiry received",
    body: "Your enquiry was submitted to the Brionj house manager.",
  });
  if (status === "in_review" || status === "contacted" || status === "converted" || status === "closed") {
    timeline.push({
      when: updatedDate || submittedDate,
      title: status === "closed" && e.withdrawn_by_guest ? "Enquiry withdrawn" : "Picked up by the house manager",
      body: status === "closed" && e.withdrawn_by_guest
        ? "You withdrew this enquiry."
        : "A house manager is reviewing your dates, party size, and preferences.",
    });
  }
  if (status === "contacted" || status === "converted") {
    timeline.push({
      when: updatedDate || submittedDate,
      title: "Proposal sent",
      body: "A tailored proposal has been prepared for your stay.",
    });
  }
  if (status === "converted") {
    timeline.push({
      when: updatedDate || submittedDate,
      title: "Confirmed",
      body: "Your residence stay has been confirmed.",
    });
  }

  // Mailto helpers — prototype-honest stand-ins for what would be real comms.
  const houseManagerEmail = "houses@brionj.com";
  const replyToEnquiry = () => {
    const subject = encodeURIComponent(`Brionj enquiry ${e.id.slice(0, 8)} — ${e.residence_name}`);
    const body = encodeURIComponent(
      `Hello Brionj team,\n\nRegarding enquiry ${e.id}\nResidence: ${e.residence_name}\nDates: ${e.check_in || "—"} → ${e.check_out || "—"}\nParty: ${e.guests} guests · ${e.bedrooms} bedrooms\n\n`
    );
    window.location.href = `mailto:${houseManagerEmail}?subject=${subject}&body=${body}`;
  };
  const requestProposal = () => {
    toast("Your request for a proposal has been noted. The house manager will be in touch.");
  };
  const contactHouseManager = () => {
    const subject = encodeURIComponent(`Brionj enquiry ${e.id.slice(0, 8)} — ${e.residence_name}`);
    window.location.href = `mailto:${houseManagerEmail}?subject=${subject}`;
  };

  return (
    <div className="space-y-6" data-testid="enquiry-detail">
      <SheetHeader className="text-left space-y-2">
        <BrandLabel>BRIONJ · Enquiry</BrandLabel>
        <SheetTitle className="font-display italic text-[26px] leading-tight">
          {e.residence_name}
        </SheetTitle>
        <SheetDescription className="text-[13px]">
          {e.residence_location}
        </SheetDescription>
        <div className="pt-2 flex items-center gap-3">
          <Badge className={statusTone[status] || statusTone.new}>{statusLabel}</Badge>
          <span className="font-mono text-[11px] text-muted-foreground" data-testid="enquiry-reference">
            REF · {e.id}
          </span>
        </div>
      </SheetHeader>

      <img
        src={e.residence_image}
        alt={e.residence_name}
        className="w-full aspect-[16/9] object-cover rounded-[var(--radius)] border border-border"
      />

      {/* ----------------- Key facts ----------------- */}
      <section className="grid grid-cols-2 gap-4 text-[13px]">
        <Fact label="Submitted"   value={fmtFull(submittedDate)} testid="enquiry-submitted" />
        <Fact label="Last update" value={fmtFull(updatedDate) || fmtFull(submittedDate)} testid="enquiry-updated" />
        <Fact label="Guest"       value={e.guest_name} testid="enquiry-guest" />
        <Fact label="Party"       value={`${e.guests} guest${e.guests !== 1 ? "s" : ""} · ${e.bedrooms} bedroom${e.bedrooms !== 1 ? "s" : ""}`} testid="enquiry-party" />
        <Fact
          label="Proposed dates"
          value={
            e.check_in && e.check_out
              ? `${fmtDate(e.check_in)} → ${fmtDate(e.check_out)}${nights ? `  (${nights} night${nights !== 1 ? "s" : ""})` : ""}`
              : "Flexible"
          }
          testid="enquiry-dates"
        />
        <Fact label="Budget band" value={e.budget || "Not stated"} testid="enquiry-budget" />
      </section>

      {(e.notes && e.notes.trim().length > 0) ? (
        <section data-testid="enquiry-notes">
          <BrandLabel>Your message</BrandLabel>
          <p className="mt-2 text-[13px] text-foreground/85 whitespace-pre-line">
            {e.notes}
          </p>
        </section>
      ) : null}

      <Separator />

      {/* ----------------- House manager ----------------- */}
      <section data-testid="enquiry-house-manager">
        <BrandLabel>House manager</BrandLabel>
        {houseManager ? (
          <div className="mt-2 text-[13px]">
            <div className="text-foreground">{houseManager}</div>
            <a href={`mailto:${houseManagerEmail}`} className="text-muted-foreground hover:text-foreground underline underline-offset-[3px]">
              {houseManagerEmail}
            </a>
          </div>
        ) : (
          <p className="mt-2 text-[13px] text-muted-foreground">
            Pending assignment. The Brionj team will introduce you once an enquiry has been reviewed.
          </p>
        )}
      </section>

      <Separator />

      {/* ----------------- Activity log ----------------- */}
      <section data-testid="enquiry-activity">
        <BrandLabel>Activity</BrandLabel>
        <ol className="mt-3 space-y-3 text-[13px] border-l border-border pl-4">
          {timeline.map((t) => (
            <li key={`${t.when}-${t.title}`} className="relative">
              <span
                className="absolute -left-[21px] top-1.5 inline-block w-2 h-2 rounded-full bg-foreground/60"
                aria-hidden="true"
              />
              <div className="text-foreground">{t.title}</div>
              <div className="text-[11.5px] text-muted-foreground mt-0.5">{fmtFull(t.when)}</div>
              {t.body && <p className="text-[12.5px] text-foreground/75 mt-1">{t.body}</p>}
            </li>
          ))}
        </ol>
      </section>

      {/* ----------------- Actions ----------------- */}
      <Separator />
      <section className="flex flex-wrap gap-2" data-testid="enquiry-actions">
        <Link
          to={`/brionj/r/${e.residence_id}`}
          className="text-[11px] uppercase tracking-[0.18em] inline-flex items-center gap-1.5 px-3 py-2 border border-border rounded-[var(--radius)] hover:border-foreground/40 hover:text-foreground transition-colors"
          onClick={onClose}
          data-testid="enquiry-view-residence"
        >
          View residence <ExternalLink size={12} />
        </Link>
        {status !== "closed" && status !== "converted" && (
          <button
            type="button"
            className="text-[11px] uppercase tracking-[0.18em] inline-flex items-center gap-1.5 px-3 py-2 border border-border rounded-[var(--radius)] hover:border-foreground/40 hover:text-foreground transition-colors"
            onClick={replyToEnquiry}
            data-testid="enquiry-reply"
          >
            Reply to enquiry <MessageSquare size={12} />
          </button>
        )}
        {status !== "closed" && status !== "converted" && (
          <button
            type="button"
            className="text-[11px] uppercase tracking-[0.18em] inline-flex items-center gap-1.5 px-3 py-2 border border-border rounded-[var(--radius)] hover:border-foreground/40 hover:text-foreground transition-colors"
            onClick={requestProposal}
            data-testid="enquiry-send-proposal"
          >
            Send proposal <FileText size={12} />
          </button>
        )}
        <button
          type="button"
          className="text-[11px] uppercase tracking-[0.18em] inline-flex items-center gap-1.5 px-3 py-2 border border-border rounded-[var(--radius)] hover:border-foreground/40 hover:text-foreground transition-colors"
          onClick={contactHouseManager}
          data-testid="enquiry-contact-manager"
        >
          Contact the house manager <Mail size={12} />
        </button>
      </section>
    </div>
  );
};


const Fact = ({ label, value, testid }) => (
  <div data-testid={testid}>
    <div className="text-[10.5px] uppercase tracking-[0.20em] text-muted-foreground font-ui">
      {label}
    </div>
    <div className="mt-1 text-foreground">{value || "—"}</div>
  </div>
);

export default Bookings;
